import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog as RadixDialog } from "radix-ui";
import { ArrowRight, Clock, Keyboard, Plus, Search, Star, UserRound } from "lucide-react";

import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/cn";
import { useDebounce } from "@/hooks/useDebounce";
import { employeeApi, employeeKeys } from "@/features/employees/api";
import { navItemAt, type NavGroup, type NavItem } from "@/app/nav";
import type { CreateAction } from "@/app/create";
import { askToCreate } from "@/lib/create";
import { useFavourites } from "@/lib/favourites";
import { useAuth } from "@/lib/auth/AuthProvider";
import { layerOpen, showShortcuts, typingIn } from "@/lib/shortcuts";

const RECENT_KEY = "hrsys.search.recent";
const RECENT_MAX = 5;

interface Hit {
  id: string;
  kind: "screen" | "person" | "recent" | "action" | "command";
  label: string;
  hint?: string;
  to: string;
  icon?: NavItem["icon"];
  /** Other words somebody might type for a screen. */
  keywords?: string;
  /** For an action: the note left for the screen it opens on. */
  ask?: string;
  /** The heading it stands under, in the list shown before anything is typed. */
  under?: string;
}

/** Not a place: it opens the sheet of shortcuts over wherever somebody is. */
const SHORTCUTS: Hit = {
  id: "command:shortcuts",
  kind: "command",
  label: "Keyboard shortcuts",
  hint: "Every key that does something",
  to: "",
  keywords: "keys hotkeys help ?",
};

/**
 * Where somebody has been, as plain words: an address, a name, a hint.
 *
 * An icon is a component and does not survive being written down, so it is
 * never stored; one that was stored by an earlier version comes back as an
 * empty object, which cannot be drawn, and is dropped here.
 */
function readRecent(owner: string): Hit[] {
  try {
    const raw = localStorage.getItem(`${RECENT_KEY}.${owner}`);
    const stored: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(stored)) return [];
    return (stored as Partial<Hit>[])
      .filter((one) => typeof one?.to === "string" && typeof one.label === "string")
      .map((one) => ({
        id: `recent:${one.to}`,
        kind: one.kind === "person" || one.kind === "screen" ? one.kind : "recent",
        label: one.label!,
        hint: typeof one.hint === "string" ? one.hint : undefined,
        to: one.to!,
      }));
  } catch {
    return [];
  }
}

function remember(owner: string, hit: Hit) {
  try {
    const kept = [
      { kind: hit.kind, label: hit.label, hint: hit.hint, to: hit.to },
      ...readRecent(owner).filter((one) => one.to !== hit.to),
    ].slice(0, RECENT_MAX);
    localStorage.setItem(`${RECENT_KEY}.${owner}`, JSON.stringify(kept));
  } catch {
    // Storage may be blocked or full; the palette works without a history.
  }
}

/** The mark before a hit: the screen's own icon, or one for its kind. */
function HitIcon({ hit, className }: { hit: Hit; className?: string }) {
  if (hit.icon) {
    const Icon = hit.icon;
    return <Icon className={className} aria-hidden="true" />;
  }
  if (hit.kind === "action") return <Plus className={className} aria-hidden="true" />;
  if (hit.kind === "command") return <Keyboard className={className} aria-hidden="true" />;
  if (hit.kind === "person") return <UserRound className={className} aria-hidden="true" />;
  if (hit.kind === "recent") return <Clock className={className} aria-hidden="true" />;
  return <ArrowRight className={className} aria-hidden="true" />;
}

/**
 * The search box.
 *
 * Seventeen screens and forty-odd people is too many to reach by clicking.
 * Type a name and land on the record; type "rota" and land on the week.
 * Opened with ⌘K or Ctrl+K anywhere, or "/" outside a field, or the button
 * in the header. Screens come from the same list the rail is built from, so
 * somebody only ever finds what they are allowed to open; people come from
 * the employee search, so HR gets them and an employee does not. Before
 * anything is typed it offers what somebody pinned, then where they last
 * went.
 */
export function CommandPalette({
  nav,
  create = [],
  canSearchPeople,
  open,
  onOpenChange,
}: {
  nav: NavGroup[];
  /** What can be started from here: "new letter", "new task". */
  create?: CreateAction[];
  canSearchPeople: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query.trim(), 150);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const { favourites } = useFavourites();
  // Recent places are kept per person: on a shared counter tablet, a name HR
  // looked up should not be offered to whoever signs in next.
  const { session } = useAuth();
  const owner = session?.user.id ?? "nobody";

  const searchPeople = canSearchPeople && debounced.length >= 2;
  const { data: people } = useQuery({
    queryKey: employeeKeys.list({ q: debounced, page_size: 8 }),
    queryFn: () => employeeApi.list({ q: debounced, page_size: 8 }),
    enabled: searchPeople,
    staleTime: 30_000,
  });

  const screens = useMemo<Hit[]>(
    () =>
      nav.flatMap((group) =>
        group.items.map((item) => ({
          id: `screen:${item.to}`,
          kind: "screen" as const,
          label: item.label,
          hint: group.heading,
          to: item.to,
          icon: item.icon,
          keywords: item.keywords,
        })),
      ),
    [nav],
  );

  const actions = useMemo<Hit[]>(
    () =>
      create.map((action) => ({
        id: `action:${action.label}`,
        kind: "action" as const,
        label: `New ${action.label.toLowerCase()}`,
        hint: "Start one",
        to: action.to,
        ask: action.ask,
        keywords: `new create add start ${action.keywords ?? ""}`,
      })),
    [create],
  );

  // A pinned screen wears its own icon, a pinned person the person's, and
  // anything else the star it was pinned with.
  const pinned = useMemo<Hit[]>(
    () =>
      favourites.map((one) => ({
        id: `favourite:${one.to}`,
        kind: one.kind,
        label: one.label,
        to: one.to,
        icon: navItemAt(nav, one.to)?.icon ?? (one.kind === "person" ? undefined : Star),
      })),
    [favourites, nav],
  );

  const hits = useMemo<Hit[]>(() => {
    const term = query.trim().toLowerCase();
    if (!term) {
      // What is pinned first, and then not a second time under "Recent".
      const elsewhere = (hit: Hit) => !pinned.some((one) => one.to === hit.to);
      const recent = readRecent(owner)
        .filter(elsewhere)
        .map((hit) => ({ ...hit, icon: navItemAt(nav, hit.to)?.icon, under: "Recent" }));
      return [
        ...pinned.map((hit) => ({ ...hit, under: "Favourites" })),
        ...(recent.length > 0
          ? recent
          : screens
              .filter(elsewhere)
              .slice(0, 8)
              .map((hit) => ({ ...hit, under: "Screens" }))),
      ];
    }
    const matches = (hit: Hit) =>
      `${hit.label} ${hit.hint ?? ""} ${hit.keywords ?? ""}`.toLowerCase().includes(term);
    const matchedScreens = [...screens, ...actions, SHORTCUTS].filter(matches);
    const matchedPeople: Hit[] =
      searchPeople
        ? (people?.items ?? []).map((employee) => ({
            id: `person:${employee.user_id}`,
            kind: "person" as const,
            label: employee.full_name_en,
            hint: [employee.employee_id, employee.position_title].filter(Boolean).join(" · "),
            to: `/admin/employees/${employee.user_id}`,
          }))
        : [];
    const found = [...matchedPeople, ...matchedScreens];
    // Something pinned that is neither a screen in the sidebar nor a person
    // the search turned up is still somewhere somebody can ask for by name.
    const matchedPinned = pinned.filter(
      (hit) => matches(hit) && !found.some((one) => one.to === hit.to),
    );
    return [...found, ...matchedPinned].slice(0, 12);
  }, [query, searchPeople, screens, actions, people, pinned, nav, owner]);

  // The active row never points past the end of a list that just shrank.
  const activeIndex = Math.min(active, Math.max(0, hits.length - 1));

  function close(next: boolean) {
    if (!next) {
      setQuery("");
      setActive(0);
    }
    onOpenChange(next);
  }

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const palette = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      // "/" is a single key like the shell's own: not in a field, and not
      // while something is already open over the page.
      const slash =
        event.key === "/" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !typingIn(event.target) &&
        !layerOpen();
      if (palette || slash) {
        event.preventDefault();
        onOpenChange(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  function go(hit: Hit) {
    if (hit.kind === "command") {
      close(false);
      showShortcuts();
      return;
    }
    // An action is not somewhere somebody has been, so it is not remembered.
    if (hit.kind === "action") {
      if (hit.ask) askToCreate(hit.ask);
    } else {
      remember(owner, hit);
    }
    close(false);
    navigate(hit.to);
  }

  function onInputKey(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(hits.length - 1, activeIndex + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(0, activeIndex - 1));
    } else if (event.key === "Enter") {
      const hit = hits[activeIndex];
      if (hit) {
        event.preventDefault();
        go(hit);
      }
    }
  }

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <RadixDialog.Root open={open} onOpenChange={close}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim/45" />
        <RadixDialog.Content
          aria-label="Search"
          className={cn(
            "fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2",
            "overflow-hidden rounded-lg bg-surface-raised shadow-pop ring-1 ring-line",
          )}
        >
          <RadixDialog.Title className="sr-only">Search</RadixDialog.Title>
          <RadixDialog.Description className="sr-only">
            Type the name of a screen{canSearchPeople ? " or a person" : ""} and press Enter.
          </RadixDialog.Description>
          <div className="flex items-center gap-3 border-b border-line-faint px-4">
            <Search className="size-4 shrink-0 text-fg-subtle" aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onInputKey}
              role="combobox"
              aria-expanded="true"
              aria-controls="command-palette-results"
              aria-activedescendant={hits[activeIndex] ? `hit-${hits[activeIndex].id}` : undefined}
              aria-autocomplete="list"
              placeholder={canSearchPeople ? "Search people and screens" : "Search screens"}
              className="h-12 w-full bg-transparent text-md text-fg outline-none placeholder:text-fg-placeholder"
            />
            <kbd className="hidden rounded-xs bg-surface-sunken px-1.5 py-0.5 text-2xs font-semibold text-fg-subtle sm:block">
              Esc
            </kbd>
          </div>

          <ul
            id="command-palette-results"
            ref={listRef}
            role="listbox"
            aria-label="Results"
            className="max-h-[50vh] overflow-y-auto p-2"
          >
            {hits.length === 0 ? (
              <li className="px-3 py-8 text-center text-base text-fg-subtle">
                Nothing by that name.
              </li>
            ) : null}
            {hits.map((hit, index) => {
              const selected = index === activeIndex;
              const heading = hit.under !== hits[index - 1]?.under ? hit.under : undefined;
              return (
                <Fragment key={hit.id}>
                {heading ? (
                  <li role="presentation" className="u-label px-3 pb-1 pt-2">
                    {heading}
                  </li>
                ) : null}
                <li
                  id={`hit-${hit.id}`}
                  role="option"
                  aria-selected={selected}
                  data-index={index}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(hit)}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-xs px-3 py-2",
                    selected ? "bg-brand-50 text-brand-700" : "text-fg",
                  )}
                >
                  <HitIcon hit={hit} className={cn("size-4 shrink-0", selected ? "text-accent" : "text-fg-subtle")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium">{hit.label}</span>
                    {hit.hint ? (
                      <span className="block truncate text-xs text-fg-subtle">{hit.hint}</span>
                    ) : null}
                  </span>
                  {selected ? (
                    <kbd className="rounded-xs bg-surface px-1.5 py-0.5 text-2xs font-semibold text-fg-subtle ring-1 ring-inset ring-line">
                      ↵
                    </kbd>
                  ) : null}
                </li>
                </Fragment>
              );
            })}
          </ul>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
